import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  createUserWithIdp,
  postMailerLite,
  postReferralRecord,
  updateUserProfileGeneral,
  signupWithRecaptcha,
} from '../util/api';
import { clearReferralCode } from '../util/referral';
import { unregisterTwinrPushToken } from '../util/twinr';
import { storableError } from '../util/errors';
import * as log from '../util/log';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// Links a freshly created account back to whoever's referral link brought them
// here (P25028-80).
//
// `referralCode` is null unless the visitor arrived on a `?ref=` link. Like the
// newsletter call above this only runs once the account exists, and it must
// never affect the sign up: the endpoint always answers 200, and the .catch()
// covers the case where it cannot be reached at all. Not awaited, so the
// redirect after sign up does not wait on it.
const recordReferral = referralCode => {
  if (!referralCode) {
    return;
  }

  postReferralRecord({ code: referralCode })
    .then(() => {
      // Recorded or rejected, this code has had its one chance — a later sign
      // up in the same browser session should not reuse it.
      clearReferralCode();
    })
    .catch(e => {
      log.error(e, 'referral-record-failed', {});
    });
};

// Adds a freshly created user to the MailerLite newsletter list.
//
// `newsletter` is null unless the optional consent on the sign up form was
// ticked, and this is only ever called once the account exists — so a failure
// here can never block a sign up or leave someone subscribed to a sign up that
// did not happen. It is therefore deliberately not awaited: the redirect after
// sign up should not wait on MailerLite.
//
// The endpoint answers HTTP 200 with `success: false` when MailerLite rejects the
// call (an expired API key, for instance), so the body has to be checked as well
// as the promise — otherwise a broken key fails silently.
const subscribeToNewsletter = newsletter => {
  if (!newsletter) {
    return;
  }

  const { email, firstName, lastName, recaptchaToken } = newsletter;

  postMailerLite({ email, firstName, lastName, recaptchaToken })
    .then(response => {
      if (!response?.success) {
        log.error(
          new Error(response?.message || 'MailerLite rejected the subscription'),
          'newsletter-subscription-failed',
          { email }
        );
      }
    })
    .catch(e => log.error(e, 'newsletter-subscription-failed', { email }));
};

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  // Inside the Twinr app, remove this phone's push token first, while the session still
  // exists. It never rejects and gives up after ~2 s, so it cannot block the logout.
  return unregisterTwinrPushToken()
    .then(() => sdk.logout())
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(signupRequest());

  const {
    token,
    email,
    password,
    firstName,
    lastName,
    charityName,
    newsletter,
    // Kept out of `rest` so the referral code does not end up stored on the
    // user's protected data — it belongs in the referral record instead.
    referralCode,
    ...rest
  } = params;

  try {
    await signupWithRecaptcha({ token });

    const createUserParams = isEmpty(rest)
      ? { email, password, firstName, lastName }
      : { email, password, firstName, lastName, protectedData: { ...rest } };

    // We must login the user if signup succeeds since the API doesn't
    // do that automatically.
    await sdk.currentUser.create(createUserParams);
    dispatch(signupSuccess());

    // Only once the account exists — a sign up that failed must not add anyone to
    // the newsletter list.
    subscribeToNewsletter(newsletter);

    await dispatch(login(email, password));

    const state = getState();
    const userId = state.user.currentUser?.id?.uuid;

    // After the login above, so the endpoint can read who the new user is from
    // their own session rather than trusting an id sent from the browser.
    recordReferral(referralCode);

    if (charityName && userId) {
      await updateUserProfileGeneral({
        params: {
          id: userId,
          metadata: {
            charityName: charityName,
          },
        },
      });
    }
    return null;
  }
  catch (e) {
    dispatch(signupError(storableError(e)));
    log.error(e, 'signup-failed', {
      email: params.email,
      firstName: params.firstName,
      lastName: params.lastName,
    });
  };
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());

  // The newsletter consent and the referral code are handled here rather than
  // sent on to the endpoint, which knows nothing about either. Keeping them out
  // of the logged params also keeps the one-time reCAPTCHA token out of the
  // error reports.
  const { newsletter, referralCode, ...createUserParams } = params;

  return createUserWithIdp(createUserParams)
    .then(res => {
      // Only once the account exists, as in the signup thunk above.
      subscribeToNewsletter(newsletter);
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .then(res => {
      // createUserWithIdp logs the user in, so by now the session the referral
      // endpoint reads the new user's id from exists.
      recordReferral(referralCode);
      return res;
    })
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params: createUserParams });
      return dispatch(confirmError(storableError(e)));
    });
};
